import { BaseAIService } from '../BaseAIService';
import { AIServiceOptions, AIServiceResponse, VLLMConfig } from '../types';
import { handleStreamResponse } from '../utils/StreamHandler';
import { parseErrorResponse } from '../utils/ErrorHandler';

// 定义VLLM服务所需配置类型
export interface VLLMServiceConfig {
  endpoint?: string;
  model?: string;
}

interface VLLMStreamResponse {
  text?: string;
  finish_reason?: string;
  choices?: Array<{
    text?: string;
    delta?: {
      content?: string;
    };
    message?: {
      content?: string;
    };
  }>;
}

export class VLLMService extends BaseAIService {
  private config: VLLMConfig;
  
  constructor(config: VLLMConfig = {}) {
    super('vllm');
    this.config = config;
  }
  
  async generateText(options: AIServiceOptions): Promise<AIServiceResponse> {
    if (!this.config.endpoint) {
      return {
        text: '',
        error: 'VLLM 服务端点未配置'
      };
    }
    
    try {
      const messages = this.createMessages(options.prompt, options.systemPrompt);
      
      // 使用chat格式的消息
      const url = `${this.config.endpoint}/v1/chat/completions`;
      const headers = {
        'Content-Type': 'application/json'
      };
      
      const body = {
        messages: messages,
        stream: !!options.onUpdate,
        max_tokens: options.maxTokens || this.config.maxTokens,
        temperature: options.temperature ?? this.config.temperature ?? 0.7,
        top_p: options.topP ?? this.config.topP ?? 1,
        model: this.config.model || 'default'
      };
      
      // 如果有实时更新回调，使用流式响应
      if (options.onUpdate) {
        const text = await handleStreamResponse({
          url,
          headers,
          body,
          signal: options.abortSignal,
          onUpdate: options.onUpdate,
          errorPrefix: 'VLLM 请求失败',
          parseContent: (data: unknown) => {
            console.log('VLLM 流式数据:', JSON.stringify(data));
            const response = data as VLLMStreamResponse;
            
            // 尝试多种可能的格式
            if (response.text) {
              return response.text;
            }
            
            if (response.choices && response.choices.length > 0) {
              const choice = response.choices[0];
              if (choice.text) return choice.text;
              if (choice.delta?.content) return choice.delta.content;
              if (choice.message?.content) return choice.message.content;
            }
            
            console.warn('VLLM: 未能从流式响应中提取文本', response);
            return '';
          }
        });
        
        return { text };
      } else {
        // 非流式响应
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal: options.abortSignal
        });
        
        if (!response.ok) {
          throw await parseErrorResponse(this.provider, response);
        }
        
        const data = await response.json();
        return {
          text: data.choices?.[0]?.message?.content || data.choices?.[0]?.text || ''
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }
} 